import { Component, HostListener, importProvidersFrom, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { bootstrapApplication } from '@angular/platform-browser';
import { initializeApp } from 'firebase/app';
import { MaterialModule } from './material/material.module';
import { MatIconRegistryService } from './services/mat-icon-registry/mat-icon-registry.service';
import { SharedModule } from './shared/shared.module';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ToasterService } from './services/toaster/toaster.service';
import { PagingService } from './services/paging/paging.service';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { appConfig } from './app.config';
import { ProjectModule } from './project/project.module';
import { CategoryListComponent } from './project/category-list/category-list.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GetLanguageService } from './services/translations/translations.service';
import { FiltersModalComponent } from './shared/filters-modal/filters-modal.component';
import { PipesModule } from './pipes/pipes.module';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SpinnerService } from './services/spinner/spinner.service';
import { AuthInterceptor } from './services/auth/auth-interceptor/auth-interceptor.service';
import { MediaLibraryService } from './services/media-library/media-library.service';
import { environment } from '../environments/environment';
import * as Sentry from "@sentry/angular";
import { HeaderMessageComponent } from './shared/header-message/header-message.component';
import { AnalyticsService } from './services/analytics/analytics.service';
import { VersionCheckService } from './services/version-check/version-check.service';
import { PresenceService } from './services/presence/presence.service';

@Component({
	selector: 'app-root',
	standalone: true,
	imports: [
		RouterOutlet,
		HttpClientModule,
		MaterialModule,
		SharedModule,
		ProjectModule,
		CategoryListComponent,
		FiltersModalComponent,
		PipesModule,
		MatProgressSpinnerModule,
		HeaderMessageComponent
	],
	providers: [
		MatIconRegistryService,
		ToasterService,
		PagingService,
		GetLanguageService,
	],
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
	@HostListener('window:beforeunload', ['$event'])
	areVideosStillProcessing($event: BeforeUnloadEvent) {
		if (Object.keys(this.mediaLibraryService?.getVideosQueueProgress()).length) {
			$event.returnValue = 'general.videos_still_processing';
		}
	}

	private matIconRegistry = inject(MatIconRegistryService);
	private getLanguageService = inject(GetLanguageService);
	private spinnerService = inject(SpinnerService);
	private mediaLibraryService = inject(MediaLibraryService);
	private translateService = inject(TranslateService);
	private analyticsService = inject(AnalyticsService);
	private versionCheckService = inject(VersionCheckService);
	private presenceService = inject(PresenceService);

	showSpinner: boolean = false;

	async ngOnInit(): Promise<void> {
		this.matIconRegistry.initializeIcons();
		this.translateService.setDefaultLang(this.getLanguageService.getDefaultLanguage(this.translateService.getBrowserLang()));
		const app = initializeApp(environment.firebase);
		this.analyticsService.init(app);
		this.presenceService.init();
		this.spinnerService.visibility.subscribe(value => {
			Promise.resolve().then(() => this.showSpinner = value)
		});
		this.versionCheckService.start();
	}
}

Sentry.init({
	dsn: environment.sentry.dns,
	environment: environment.sentry.env,
	integrations: [
		Sentry.replayIntegration(),
		// Services deliberately swallow Firestore failures with console.error so a
		// single bad read can't blank the page. This forwards every console.error
		// to Sentry (with the Error's stack when one is passed) so those failures
		// are still visible in the field.
		Sentry.captureConsoleIntegration({ levels: ['error'] })
	],
	replaysSessionSampleRate: environment.sentry.replaysSessionSampleRate,
	replaysOnErrorSampleRate: environment.sentry.replaysOnErrorSampleRate
});

bootstrapApplication(AppComponent, {
	providers: [
		importProvidersFrom(HttpClientModule, TranslateModule.forRoot()),
		provideAnimations(),
		appConfig.providers,
		PipesModule,
		{ provide: FIREBASE_OPTIONS, useValue: environment.firebase },
		HttpClientModule,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthInterceptor,
			multi: true
		}
	],
});
